<script setup lang="ts">
import {
  computed,
  ref,
  defineEmits,
  watch,
  nextTick,
  useTemplateRef,
  capitalize,
  withDefaults,
  defineProps,
} from "vue";
import { useI18n } from "vue-i18n";
import ArrowIcon from "@/components/icons/ArrowIcon.vue";
import ChevronIcon from "@/components/icons/ChevronIcon.vue";
import CalendarIcon from "@/components/icons/CalendarIcon.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import { onlyDigits } from "@/utils/validators";

const props = withDefaults(
  defineProps<{
    showDatePickIcon?: boolean;
    styleType?: "filled" | "outline";
    initialDate?: string;
    minDate?: Date;
    maxDate?: Date;
  }>(),
  {
    showDatePickIcon: true,
    styleType: "filled",
  }
);

const emits = defineEmits<{
  (e: "dateSelected", newDate: Date): void;
}>();

watch(
  () => props.initialDate,
  (newDate: string | undefined) => {
    if (newDate === undefined) {
      date.value = undefined;
      return;
    }
    date.value = new Date(newDate);
  }
);

const { locale, t } = useI18n();

const dpRef = useTemplateRef<InstanceType<typeof VueDatePicker>>("datepicker");
const showYearOverlay = ref(false);
const date = ref();
const manualDate = ref<string>("");
const formattedDate = computed(() => {
  if (!date.value) return "";
  return date.value.toLocaleDateString(locale.value, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
});

watch(showYearOverlay, async () => {
  if (showYearOverlay.value) {
    await nextTick();
    document
      .querySelector(".current-year-item")
      ?.scrollIntoView({ behavior: "auto", block: "center" });
  }
});

const dateSelected = (selectedDate: Date) => {
  date.value = selectedDate;
  emits("dateSelected", selectedDate);
};

function getFullMonthName(year: number, month: number) {
  const date = new Date(year, month + 1, 0);

  return capitalize(date.toLocaleDateString(locale.value, { month: "long" }));
}

function selectYear(year: number) {
  dpRef.value?.setMonthYear({ year: year });
  showYearOverlay.value = false;
}

function validateManualDate(e: KeyboardEvent) {
  if (!onlyDigits(e.key, e.ctrlKey)) {
    e.preventDefault();
  }
}

function manualDateInput(e: Event) {
  const target = e.target as HTMLInputElement;
  const digits = target.value.replace(/\D/g, "");
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);

  if (day.length !== 2 || month.length !== 2 || year.length !== 4) {
    updateManualDateValue(day, month, year);
    if (date.value) {
      date.value = null;
    }
  } else {
    normalizeAndSetDate(day, month, year);
  }
}

function updateManualDateValue(day: string, month: string, year: string) {
  let newManualDate = day;
  if (month.length > 0) {
    newManualDate += (day.length === 2 ? "." : "") + month;
  }
  if (year.length > 0) {
    newManualDate += (month.length === 2 ? "." : "") + year;
  }
  manualDate.value = newManualDate;
}

function normalizeAndSetDate(day: string, month: string, year: string) {
  let newDate = new Date();
  // Normalize Year
  const normalizeYear = Number(year) < 1900 ? 1900 : Number(year);
  newDate.setFullYear(normalizeYear);
  // Normalize month
  const normalizedMonth = Number(month) > 12 ? 12 : Number(month);
  newDate.setMonth(normalizedMonth - 1);
  // Normalize day
  const maxDay = new Date(
    newDate.getFullYear(),
    newDate.getMonth() + 1,
    0
  ).getDate();
  const normalizedDay = Number(day) > maxDay ? maxDay : Number(day);
  newDate.setDate(normalizedDay);

  if (props.minDate && newDate < props.minDate) {
    newDate = props.minDate;
  }
  if (props.maxDate && newDate > props.maxDate) {
    newDate = props.maxDate;
  }

  dateSelected(newDate);
}
</script>

<template>
  <div
    class="flex items-center border border-divider pl-4 pr-[6px] w-full h-[52px] rounded-[14px] font-normal text-base"
    :class="{
      'bg-secondary text-muted': styleType === 'filled',
      'border-muted text-black': styleType === 'outline',
      'cursor-pointer': showDatePickIcon,
    }"
  >
    <input
      :value="date ? formattedDate : manualDate"
      :placeholder="t('placeholders.date_placeholder')"
      class="flex-1 bg-transparent outline-none w-full sf-pro"
      type="text"
      maxlength="10"
      autocomplete="off"
      @keydown="validateManualDate"
      @input="manualDateInput"
    />
    <VueDatePicker
      v-if="showDatePickIcon"
      ref="datepicker"
      :model-value="date"
      @update:model-value="dateSelected"
      :enable-time-picker="false"
      :ui="{ calendarCell: ['m-1'] }"
      auto-apply
      :locale="locale"
      class="!w-auto"
      :teleport="true"
      :min-date="props.minDate"
      :max-date="props.maxDate"
    >
      <template #trigger>
        <div
          class="flex items-center justify-center size-10 rounded-lg bg-brown-gradient hover:bg-orange-gradient text-white"
        >
          <CalendarIcon />
        </div>
      </template>
      <!-- HEADER -->
      <template #month-year="{ month, year, handleMonthYearChange, years }">
        <div class="w-full border-b border-ternary flex justify-between">
          <div
            class="flex pl-[11px] pt-[17px] pb-[15px] cursor-pointer"
            @click="showYearOverlay = !showYearOverlay"
          >
            <p class="font-semibold text-xl leading-100p text-active">
              {{ getFullMonthName(year, month) }} {{ year }}
            </p>
            <ChevronIcon class="text-active pl-2" />
          </div>
          <div class="flex gap-x-4 mt-[16px] mr-[10px] mb-[16px]">
            <ArrowIcon
              class="transform-gpu -rotate-45 cursor-pointer text-brown"
              @click="handleMonthYearChange(false)"
            />
            <ArrowIcon
              class="transform-gpu rotate-[135deg] cursor-pointer text-brown"
              @click="handleMonthYearChange(true)"
            />
          </div>
          <div
            v-if="showYearOverlay"
            class="w-full h-full absolute top-0 left-0 z-[99999] overflow-auto no-scrollbar bg-white grid grid-cols-3 p-2 gap-1 overlay"
          >
            <div
              v-for="overlayYear in years"
              :key="overlayYear"
              class="h-12 hover:cursor-pointer rounded-md text-brown flex justify-center items-center"
              :class="
                overlayYear.value === year
                  ? 'bg-active text-white current-year-item'
                  : 'hover:bg-active hover:bg-opacity-10 hover:text-active'
              "
              @click="selectYear(overlayYear.value)"
            >
              {{ overlayYear.value }}
            </div>
          </div>
        </div>
      </template>
      <!-- WEEK NAMES -->
      <template #calendar-header="{ day }">
        <div
          class="font-semibold text-base leading-[21px] size-[42px] m-1 flex content-center justify-center flex-wrap"
        >
          {{ capitalize(day) }}
        </div>
      </template>
      <!-- CALENDAR -->
      <template #day="{ day }">
        <div class="w-full font-medium text-lg leading-[22px]">
          {{ day }}
        </div>
      </template>
      <!-- ACTION BUTTONS -->
      <template #action-extra>
        <div class="w-full px-3 pb-5 flex justify-between gap-x-2">
          <button
            class="h-[50px] w-[167px] rounded-full border border-brown text-brown font-semibold text-[17px] leading-[22px]"
            @click.prevent="date = null"
          >
            {{ t("buttons.calendar.clear") }}
          </button>
          <button
            class="h-[50px] w-[167px] rounded-full border border-active text-active font-semibold text-[17px] leading-[22px]"
            @click="dateSelected(new Date())"
          >
            {{ t("buttons.calendar.today") }}
          </button>
        </div>
      </template>
    </VueDatePicker>
  </div>
</template>

<style>
.dp__calendar_header_separator {
  display: none;
}
.dp__theme_light {
  --dp-cell-size: 42px;
  --dp-cell-padding: 0px;
  --dp-primary-color: #ee441a !important;
  --dp-cell-border-radius: 100px !important;
  --dp-hover-color: #fef6f4 !important;
  --dp-hover-text-color: #ee441a !important;
}
.dp__today {
  border: none !important;
}
</style>
